import { BRAND } from "constants/brand";
import DateTimeField from "components/Ui/DateTimeField/DateTimeField";
import TimeField from "components/Ui/TimeField/TimeField";
import FieldHelp from "components/Ui/FieldHelp/FieldHelp";
import { useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/free-solid-svg-icons";
import { Form } from "react-bootstrap";
import { CSSTransition } from "react-transition-group";
import { Ring } from "@uiball/loaders";
import { Switch } from "@mui/material";
import Select from "components/Ui/SelectField/SelectField";
// The sections, the switch rows and the notes of a settings window.
import "components/Cities/CitySettingsModal/citySettingsModal.scss";
import SelectFieldHeader from "../SelectFieldHeader/SelectFieldHeader";
import {
    BENEFIT_NUMBER_FIELD,
    BENEFIT_OPTIONS,
    CUSTOMER_COUNT_LABEL,
    CUSTOMER_RULE_OPTIONS,
    DAY_OPTIONS,
    FUNDER_OPTIONS,
    OFFER_CUSTOMER_RULE,
    formHasCondition,
    hoursPassMidnight,
} from "../deliveryOfferModel";
import "./deliveryOfferForm.scss";

/**
 * The window of a delivery offer: the fields of the add form and of the edit
 * form. The two forms keep the state and send the request.
 *
 * The fields are in four parts, one for each question that an offer answers:
 * what the customer gets, where, which customers, and when.
 *
 * @param {object} props
 * @param {boolean} props.show
 * @param {() => void} props.close
 * @param {string} props.title
 * @param {string} props.submitLabel
 * @param {boolean} props.pending - a request is on its way
 * @param {import("../deliveryOfferModel").OfferForm} props.form
 * @param {(patch: Partial<import("../deliveryOfferModel").OfferForm>) => void} props.onChange
 * @param {(event: React.FormEvent) => void} props.onSubmit
 * @param {{value: number, label: string}[]} props.vendorOptions
 * @param {{value: number, label: string}[]} props.areaOptions
 * @param {{id: number, name: string}[]} [props.cities] - only when the admin picks the city
 */
const DeliveryOfferForm = ({
    show,
    close,
    title,
    submitLabel,
    pending,
    form,
    onChange,
    onSubmit,
    vendorOptions,
    areaOptions,
    cities,
}) => {
    const nodeRef = useRef();
    const hasCity = !!form.cityId;
    const numberField = BENEFIT_NUMBER_FIELD[form.benefitType];
    const countLabel = CUSTOMER_COUNT_LABEL[form.customerRule];

    return (
        <div className={`model ${show ? "model-show" : ""}`} onClick={close}>
            <CSSTransition
                mountOnEnter
                unmountOnExit
                nodeRef={nodeRef}
                in={show}
                timeout={400}
                classNames="show"
            >
                <div
                    className="addItem city-settings-modal delivery-offer-form"
                    ref={nodeRef}
                    onClick={(e) => e.stopPropagation()}
                >
                    {pending ? (
                        <div className="loading-spinner center">
                            <Ring size={40} lineWeight={5} speed={2} color={BRAND} />
                        </div>
                    ) : null}
                    <div className="head center">
                        <h5>{title}</h5>
                        <button
                            type="button"
                            className="city-settings-modal__close"
                            onClick={close}
                        >
                            <FontAwesomeIcon icon={faClose} />
                        </button>
                    </div>
                    <Form className="form" onSubmit={onSubmit}>
                        <Form.Group className="mb-3" controlId="offerName">
                            <Form.Label>
                                اسم العرض<span>*</span>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="مثال: خصم توصيل نهايه الاسبوع"
                                value={form.name}
                                onChange={(e) => onChange({ name: e.target.value })}
                                required
                            />
                        </Form.Group>

                        {/* ---------------------------------------------- what */}
                        <div className="city-settings-modal__section">
                            <div className="city-settings-modal__section-title">
                                ماذا يحصل الزبون
                            </div>
                            <div className="delivery-offer-form__pair">
                                <Form.Group className="mb-3" controlId="offerBenefit">
                                    <Form.Label>
                                        نوع العرض<span>*</span>
                                    </Form.Label>
                                    <Form.Select
                                        value={form.benefitType}
                                        onChange={(e) => onChange({ benefitType: e.target.value })}
                                    >
                                        {BENEFIT_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                                {numberField ? (
                                    <Form.Group className="mb-3" controlId="offerAmount">
                                        <Form.Label>
                                            {numberField.label}
                                            <span>*</span>
                                        </Form.Label>
                                        <Form.Control
                                            type="number"
                                            min={numberField.step}
                                            max={numberField.max}
                                            step={numberField.step}
                                            placeholder={numberField.placeholder}
                                            value={form.amount}
                                            onChange={(e) => onChange({ amount: e.target.value })}
                                            required
                                        />
                                    </Form.Group>
                                ) : null}
                            </div>
                            <Form.Group className="mb-3" controlId="offerFunder">
                                <Form.Label>
                                    على حساب
                                    <FieldHelp label="عن من يتحمل الخصم">
                                        من يتحمل قيمه الخصم. يحفظ مع كل طلب حصل على العرض. لا يغير
                                        كشف الحساب ولا المحفظه حاليا.
                                    </FieldHelp>
                                </Form.Label>
                                <Form.Select
                                    value={form.fundedBy}
                                    onChange={(e) => onChange({ fundedBy: e.target.value })}
                                >
                                    {FUNDER_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </div>

                        {/* --------------------------------------------- where */}
                        <div className="city-settings-modal__section">
                            <div className="city-settings-modal__section-title">أين</div>
                            {cities ? (
                                <Form.Group className="mb-3" controlId="offerCity">
                                    <Form.Label>
                                        المدينه<span>*</span>
                                    </Form.Label>
                                    <Form.Select
                                        value={form.cityId}
                                        onChange={(e) =>
                                            onChange({
                                                cityId: e.target.value,
                                                vendors: [],
                                                areas: [],
                                            })
                                        }
                                    >
                                        <option value="" disabled>
                                            اختر المدينة
                                        </option>
                                        {cities.map((city) => (
                                            <option key={city.id} value={city.id}>
                                                {city.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            ) : null}
                            <Form.Group className="mb-3" controlId="offerVendors">
                                <div className="city-settings-modal__toggle delivery-offer-form__each">
                                    <Form.Label htmlFor="offerAllVendors">
                                        كل مطاعم المدينه
                                    </Form.Label>
                                    <Switch
                                        id="offerAllVendors"
                                        color="warning"
                                        size="small"
                                        checked={form.allVendors}
                                        onChange={(e) => onChange({ allVendors: e.target.checked })}
                                    />
                                    <FieldHelp label="عن كل مطاعم المدينه">
                                        يشمل العرض كل مطعم في المدينه، ومنها المطاعم التي تضاف
                                        لاحقا. «اختر الكل» يختار مطاعم اليوم فقط.
                                    </FieldHelp>
                                </div>
                                {form.allVendors ? null : (
                                    <>
                                        <SelectFieldHeader
                                            label="المطاعم"
                                            required
                                            options={vendorOptions}
                                            selected={form.vendors}
                                            onSelectAll={(vendors) => onChange({ vendors })}
                                            onClear={() => onChange({ vendors: [] })}
                                        />
                                        <Select
                                            value={form.vendors}
                                            options={vendorOptions}
                                            placeholder={
                                                hasCity ? "اختر المطاعم" : "اختر المدينة أولا"
                                            }
                                            isMulti
                                            onChange={(vendors) => onChange({ vendors })}
                                        />
                                    </>
                                )}
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="offerAreas">
                                <div className="city-settings-modal__toggle delivery-offer-form__each">
                                    <Form.Label htmlFor="offerAllAreas">كل المناطق</Form.Label>
                                    <Switch
                                        id="offerAllAreas"
                                        color="warning"
                                        size="small"
                                        checked={form.allAreas}
                                        onChange={(e) => onChange({ allAreas: e.target.checked })}
                                    />
                                    <FieldHelp label="عن كل المناطق">
                                        يشمل العرض كل منطقه يوصل لها المطعم، ومنها المناطق التي تضاف
                                        لاحقا.
                                    </FieldHelp>
                                </div>
                                {form.allAreas ? null : (
                                    <>
                                        <SelectFieldHeader
                                            label="المناطق"
                                            required
                                            options={areaOptions}
                                            selected={form.areas}
                                            onSelectAll={(areas) => onChange({ areas })}
                                            onClear={() => onChange({ areas: [] })}
                                        />
                                        <Select
                                            value={form.areas}
                                            options={areaOptions}
                                            placeholder={
                                                hasCity ? "اختر المناطق" : "اختر المدينة أولا"
                                            }
                                            isMulti
                                            onChange={(areas) => onChange({ areas })}
                                        />
                                    </>
                                )}
                            </Form.Group>
                        </div>

                        {/* ----------------------------------------------- who */}
                        <div className="city-settings-modal__section">
                            <div className="city-settings-modal__section-title">لمن</div>
                            <Form.Group className="mb-3" controlId="offerCustomerRule">
                                <Form.Label>الزبائن</Form.Label>
                                <Form.Select
                                    value={form.customerRule}
                                    onChange={(e) => onChange({ customerRule: e.target.value })}
                                >
                                    {CUSTOMER_RULE_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            <div className="delivery-offer-form__pair">
                                {countLabel ? (
                                    <Form.Group className="mb-3" controlId="offerCustomerCount">
                                        <Form.Label>
                                            {countLabel}
                                            <span>*</span>
                                            <FieldHelp label="عن عدد الطلبات">
                                                {form.customerRule ===
                                                OFFER_CUSTOMER_RULE.FIRST_ORDERS
                                                    ? "مثال: 3 يعني أول ثلاث طلبات للزبون في هذه المدينه. لا تحسب الطلبات الملغاه."
                                                    : "مثال: 5 يعني الطلب السادس للزبون في هذه المدينه، مره واحده فقط. لا تحسب الطلبات الملغاه."}
                                            </FieldHelp>
                                        </Form.Label>
                                        <Form.Control
                                            type="number"
                                            min={1}
                                            step={1}
                                            placeholder="مثال: 3"
                                            value={form.customerOrderCount}
                                            onChange={(e) =>
                                                onChange({ customerOrderCount: e.target.value })
                                            }
                                            required
                                        />
                                    </Form.Group>
                                ) : null}
                                {form.customerRule === OFFER_CUSTOMER_RULE.NTH_ORDER ? null : (
                                    <Form.Group className="mb-3" controlId="offerMaxUses">
                                        <Form.Label>
                                            مرات الاستخدام لكل زبون (اختياري)
                                            <FieldHelp label="عن مرات الاستخدام">
                                                أقصى عدد من الطلبات التي يحصل فيها الزبون الواحد على
                                                هذا العرض. الطلب الملغى يحسب. اتركه فارغا لعدد غير
                                                محدود.
                                            </FieldHelp>
                                        </Form.Label>
                                        <Form.Control
                                            type="number"
                                            min={1}
                                            step={1}
                                            placeholder="غير محدود"
                                            value={form.maxUsesPerCustomer}
                                            onChange={(e) =>
                                                onChange({ maxUsesPerCustomer: e.target.value })
                                            }
                                        />
                                    </Form.Group>
                                )}
                            </div>
                            <Form.Group className="mb-3" controlId="offerMinOrder">
                                <Form.Label>الحد الأدنى للطلب (₪) (اختياري)</Form.Label>
                                <Form.Control
                                    type="number"
                                    min={0}
                                    step={0.5}
                                    placeholder="مثال: 100"
                                    value={form.minOrderTotal}
                                    onChange={(e) => onChange({ minOrderTotal: e.target.value })}
                                />
                            </Form.Group>
                            {formHasCondition(form) ? (
                                <p className="city-settings-modal__hint">
                                    عرض له شرط على السله أو على الزبون. يظهر السعر الجديد للزبون في
                                    السله، وليس على بطاقه المطعم.
                                </p>
                            ) : null}
                        </div>

                        {/* ---------------------------------------------- when */}
                        <div className="city-settings-modal__section">
                            <div className="city-settings-modal__section-title">متى</div>
                            <div className="delivery-offer-form__pair">
                                <Form.Group className="mb-3" controlId="offerStartDate">
                                    <Form.Label>تاريخ البدايه (اختياري)</Form.Label>
                                    <DateTimeField
                                        value={form.startDate}
                                        onChange={(startDate) => onChange({ startDate })}
                                        edge="start"
                                        dateOnly
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3" controlId="offerEndDate">
                                    <Form.Label>تاريخ النهايه (اختياري)</Form.Label>
                                    <DateTimeField
                                        value={form.endDate}
                                        onChange={(endDate) => onChange({ endDate })}
                                        edge="end"
                                        dateOnly
                                    />
                                </Form.Group>
                            </div>
                            <Form.Group className="mb-3" controlId="offerDays">
                                <Form.Label>أيام العرض (اختياري)</Form.Label>
                                <Select
                                    inputId="offerDays"
                                    value={form.days}
                                    options={DAY_OPTIONS}
                                    placeholder="كل الأيام"
                                    isMulti
                                    onChange={(days) => onChange({ days })}
                                />
                            </Form.Group>
                            <div className="delivery-offer-form__pair">
                                <Form.Group className="mb-3">
                                    <Form.Label htmlFor="offerStartTime">
                                        من الساعه (اختياري)
                                    </Form.Label>
                                    <TimeField
                                        inputId="offerStartTime"
                                        className="w-100"
                                        value={form.startTime}
                                        onChange={(startTime) => onChange({ startTime })}
                                        placeholder="كل اليوم"
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label htmlFor="offerEndTime">
                                        إلى الساعه (اختياري)
                                    </Form.Label>
                                    <TimeField
                                        inputId="offerEndTime"
                                        className="w-100"
                                        value={form.endTime}
                                        onChange={(endTime) => onChange({ endTime })}
                                        placeholder="كل اليوم"
                                    />
                                </Form.Group>
                            </div>
                            {hoursPassMidnight(form) ? (
                                <p className="city-settings-modal__hint">
                                    ينتهي العرض في اليوم التالي. يحسب اليوم من وقت البدايه.
                                </p>
                            ) : null}
                            <Form.Group className="mb-3" controlId="offerActive">
                                <div className="city-settings-modal__toggle">
                                    <Form.Label>تفعيل العرض</Form.Label>
                                    <Switch
                                        color="warning"
                                        size="small"
                                        checked={form.active}
                                        onChange={(e) => onChange({ active: e.target.checked })}
                                    />
                                </div>
                            </Form.Group>
                        </div>

                        <div className="buttons">
                            <button className="cancel" onClick={close} type="button">
                                إلغاء
                            </button>
                            <button className="button" type="submit">
                                {submitLabel}
                            </button>
                        </div>
                    </Form>
                </div>
            </CSSTransition>
        </div>
    );
};

export default DeliveryOfferForm;
